import CronParser from 'cron-parser';
import cronstrue from 'cronstrue';

const CRON_TIMEZONE = 'UTC';

/**
 * Calculates the next run time from a cron expression, evaluated in UTC like the API scheduler
 * @param cronExpression - Unix cron expression (5 fields: minute hour day month weekday)
 * @param fromDate - Date to calculate from (defaults to now)
 * @returns Date object representing the next run time, or null if invalid
 */
export function getNextRunTime(cronExpression: string, fromDate: Date = new Date()): Date | null {
  try {
    const interval = CronParser.parse(cronExpression, {
      currentDate: fromDate,
      tz: CRON_TIMEZONE,
    });
    const nextRun = interval.next().toDate();
    return nextRun;
  } catch (error) {
    console.error('Error calculating next run time:', error);
    return null;
  }
}

/**
 * Describes a cron expression in words, naming the timezone it runs in
 * @param cronExpression - Unix cron expression (5 fields: minute hour day month weekday)
 * @returns Human-readable schedule, e.g. "At 05:00 PM (UTC)"
 * @throws If the expression is invalid
 */
export function describeCronSchedule(cronExpression: string): string {
  return `${cronstrue.toString(cronExpression)} (${CRON_TIMEZONE})`;
}

/**
 * Formats a date for display in a user-friendly way
 * @param date - Date to format
 * @returns Formatted string
 */
export function formatNextRunTime(date: Date): string {
  const now = new Date();
  const diffInMinutes = Math.floor((date.getTime() - now.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) {
    return 'Now';
  } else if (diffInMinutes < 60) {
    return `In ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
  } else if (diffInMinutes < 1440) {
    // Less than 24 hours
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    if (minutes === 0) {
      return `In ${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `In ${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  } else {
    const days = Math.floor(diffInMinutes / 1440);
    const hours = Math.floor((diffInMinutes % 1440) / 60);
    if (hours === 0) {
      return `In ${days} day${days > 1 ? 's' : ''}`;
    } else {
      return `In ${days} day${days > 1 ? 's' : ''} and ${hours} hour${hours > 1 ? 's' : ''}`;
    }
  }
}
